/**
 * Eventbrite checkout modal wiring for the UTMB schedule CTAs — progressive
 * enhancement over the baseline <a target="_blank"> links.
 * Spec: docs/utmb-schedule-spec.md §6 — docs/utmb-schedule-implementation.md §1.4/§3.
 *
 * EBWidgets mechanics this relies on (verified against eb_widgets.js v3.5.1):
 * - createWidget({ modal: true }) requires modalTriggerElementId and binds a
 *   document-level bubble-phase click listener delegated on that element id.
 * - On modal close the iframe is removed from the DOM; EBWidgets re-creates it
 *   on the next trigger click. One createWidget call per trigger element is
 *   therefore enough for the page's lifetime.
 */
const CTA_SELECTOR = 'a[data-eventbrite-event-id]';
// CTAs already handed to EBWidgets — see createWidget note above. Keyed per
// ELEMENT, not per event id: EBWidgets' delegated listener only matches the
// exact modalTriggerElementId it was created with, so two CTAs sharing one
// event id (e.g. test data) each need their own createWidget call — a
// per-event-id cache would leave every CTA after the first one dead.
const wiredCtas = new WeakSet();
/**
 * Registration-complete hook — intentionally a no-op in this ticket; EBWidgets
 * invokes it with { orderId }. The dataLayer push ships with the tracking
 * ticket: candidate event `eventbrite_registration` in docs/tracking-plan.md
 * open items.
 */
function onOrderComplete() { }
/**
 * Capture phase is load-bearing: EBWidgets delegates its trigger listener on
 * `document` in the bubble phase. The DOM clones a node's listener list per
 * phase invocation, so a listener added to `document` from a bubble-phase
 * `document` listener would be skipped for the current event — the first click
 * would wire the widget but not open it. From the capture phase, the listener
 * EBWidgets adds here still runs when this same click bubbles back up to
 * `document`, and the modal opens with no re-dispatch.
 */
document.addEventListener('click', (event) => {
    const cta = event.target?.closest(CTA_SELECTOR);
    if (!cta || !cta.id)
        return;
    // Capability check at click time: script blocked (adblocker, Cookiebot
    // auto-blocking) or not yet loaded — leave the baseline link untouched
    const widgets = window.EBWidgets;
    if (!widgets)
        return;
    const eventId = cta.dataset.eventbriteEventId;
    if (!eventId)
        return;
    // From here the modal owns the interaction — never also open the tab
    event.preventDefault();
    // Already wired: EBWidgets' own delegated listener opens the modal
    if (wiredCtas.has(cta))
        return;
    wiredCtas.add(cta);
    // Safe even when two CTAs share an event id: each gets an independent
    // delegated listener; EBWidgets keys modal state by event id and only
    // one modal can be open at a time (verified against eb_widgets.js).
    widgets.createWidget({
        widgetType: 'checkout',
        eventId,
        modal: true,
        modalTriggerElementId: cta.id,
        onOrderComplete,
    });
}, true);
